import {
  countryById,
  Country,
  getCountriesByIds,
  countries,
  LANGUAGE_GROUPS,
} from '../../utils/country';
import { getCategoryById } from '../Entities/Categories/categories.reducer';
import { Action } from 'redux';
import { ImmutableObject } from 'seamless-immutable';
import { isType } from 'typescript-fsa/lib';
import * as Immutable from 'seamless-immutable';
import {
  addAppConfig,
  addProducts,
  addSourceCategoryForAppId,
  editStoryFetched,
  newProductForAppId,
  removeProductIdForAppConfig,
  resetActiveStory,
  resetStoryEdit,
  startLoading,
  stopLoading,
  updateElementRow,
  updateStoryEditActiveAppId,
  updateStoryEditActiveDates,
  updateStoryEditBoostDates,
  updateStoryEditSettings,
  updateStoryEditSourceCategories,
  updateStoryEditTargetCategories,
  updateStreamProductCount,
  updateTargetCategoryIsPinned,
  updateTeaser,
  updateTeaserVideo,
  unpublishStoryAppConfig,
  removeOutfitIdForAppConfig,
  addOutfits,
  newOutfitForAppId,
  updateStoryEditRebrushedAt,
  changeResourceTypeAndResourceId,
  updateProductIdsForAppId,
} from './story-edit.actions';
import {
  AppConfig,
  Row,
  Story,
  TargetCategory,
  SourceCategory,
  StoryError,
  Element,
} from '../Entities/Stories/stories.reducer';
import { AppState } from '../reducers';
import { Cluster } from '../Entities/Clusters/clusters.reducer';
import { showClusterEditLoading } from '../ClusterEdit/cluster-edit.actions';
import { stopAllLoaders } from '../Config/config.actions';
import {
  concat,
  indexBy,
  prop,
  findIndex,
  propEq,
  update,
  merge,
  append,
  reject,
  map,
  pick,
  keys,
  differenceWith,
  pluck,
  forEachObjIndexed,
  mapObjIndexed,
  filter,
  forEach,
  contains,
} from 'ramda';
import * as R from 'ramda';

export interface StoryEdit {
  activeAppId: number;
  story: Story;
  appConfigs: {
    [key: number]: AppConfig;
  };
  row: {
    [key: number]: Row;
  };
  clusters: {
    [key: number]: Cluster;
  };
  products: {
    [key: number]: number[];
  };

  outfits: {
    [key: number]: number[];
  };
  streamProductCount: {
    [key: number]: number;
  };
  loading: boolean;
}

export type StoryEditState = StoryEdit & ImmutableObject<StoryEdit>;
const initialState = Immutable.from<StoryEdit>({
  activeAppId: 139,
  story: {} as Story,
  appConfigs: {},
  clusters: {},
  row: {},
  products: {},
  outfits: {},
  loading: false,
  streamProductCount: {},
});

export default function reduce(
  state: StoryEditState = initialState,
  action: Action
) {
  if (isType(action, resetStoryEdit)) {
    return initialState;
  }

  if (isType(action, editStoryFetched)) {
    const { payload } = action;
    const { appConfigs, story, cluster } = payload;

    return state.merge(
      {
        story,
        appConfigs,
        clusters: cluster || {},
      },
      { deep: true }
    );
  }

  if (isType(action, addSourceCategoryForAppId)) {
    const { payload } = action;
    const { appConfigs } = state;
    const { categories, appId } = payload;

    if (!appConfigs[appId]) {
      return state;
    }

    const nextSourceCategories = appConfigs[appId].sourceCategories
      ? appConfigs[appId].sourceCategories!.reduce((acc, c: SourceCategory) => {
          if (
            categories.find(category => category.categoryId === c.categoryId)
          ) {
            return acc;
          }

          return [...acc, c];
        }, [])
      : [];

    return state.merge(
      {
        appConfigs: {
          [appId]: {
            sourceCategories: concat(categories, nextSourceCategories),
          },
        },
      },
      { deep: true }
    );
  }

  if (isType(action, updateStoryEditTargetCategories)) {
    const {
      payload: { appId, targetCategories },
    } = action;
    return state.merge(
      {
        appConfigs: {
          [appId]: {
            targetCategories,
          },
        },
      },
      { deep: true }
    );
  }

  if (isType(action, updateStoryEditActiveDates)) {
    const {
      payload: { appId, active_from, active_to },
    } = action;
    return state.merge(
      {
        appConfigs: {
          [appId]: {
            active_from,
            active_to,
          },
        },
      },
      { deep: true }
    );
  }

  if (isType(action, updateStoryEditBoostDates)) {
    const {
      payload: { appId, boost_from, boost_to },
    } = action;
    return state.merge(
      {
        appConfigs: {
          [appId]: {
            boost_from,
            boost_to,
          },
        },
      },
      { deep: true }
    );
  }

  if (isType(action, updateStoryEditRebrushedAt)) {
    const {
      payload: { appId, rebrushed_at },
    } = action;
    return state.merge(
      { appConfigs: { [appId]: { rebrushed_at } } },
      { deep: true }
    );
  }

  if (isType(action, startLoading)) {
    return state.merge({ loading: true });
  }

  if (isType(action, stopLoading) || isType(action, stopAllLoaders)) {
    return state.merge({ loading: false });
  }

  if (isType(action, updateStoryEditSourceCategories)) {
    const {
      payload: { appId, sourceCategories },
    } = action;
    return state.merge(
      {
        appConfigs: {
          [appId]: {
            sourceCategories,
          },
        },
      },
      { deep: true }
    );
  }

  if (isType(action, updateTeaser)) {
    const { payload } = action;

    return state.merge(
      {
        story: { ...payload },
      },
      { deep: true }
    );
  }

  if (isType(action, updateTeaserVideo)) {
    const { payload } = action;

    return state.merge(
      {
        story: { ...payload },
      },
      { deep: true }
    );
  }

  if (isType(action, updateElementRow)) {
    const { payload } = action;

    return state.merge(
      {
        story: {
          rows: payload,
        },
      },
      { deep: true }
    );
  }

  if (isType(action, updateStoryEditActiveAppId)) {
    const {
      payload: { appId },
    } = action;
    return state.merge({ activeAppId: appId }, { deep: true });
  }

  if (isType(action, resetActiveStory)) {
    return initialState;
  }

  if (isType(action, addAppConfig)) {
    const {
      payload,
      payload: { app_id },
    } = action;
    return state.merge(
      { appConfigs: { [app_id]: { ...payload } } },
      { deep: true }
    );
  }
  if (isType(action, updateStoryEditSettings)) {
    const {
      payload: {
        storyName,
        storyColor,
        ageMax,
        ageMin,
        gender,
        storyType,
        contentType,
        appConfigId,
        clusters,
        selectedClusters,
        resourceId,
        resourceType,
        isPrimary,
        isSponsored,
        isBasSponsored,
        isSale,
        isPremium,
        isHidden,
        isProductRowDisabled,
        isSecondhand,
      },
    } = action;

    return state.merge(
      {
        story: {
          name: storyName,
          color: storyColor,
          age_from: ageMin,
          age_to: ageMax,
          gender,
          contentType,
          type: storyType,
          initialAppId: appConfigId,
          clusters: clusters,
          resourceId,
          resourceType,
          isPrimary,
          isSponsored,
          isBasSponsored,
          isSale,
          isPremium,
          isHidden,
          isProductRowDisabled,
          isSecondhand,
        },
        clusters: indexBy(prop('id'), selectedClusters || []),
      },
      { deep: true }
    );
  }

  if (isType(action, addProducts)) {
    const {
      payload: { appId, productIds },
    } = action;
    const { products } = state;

    return state.merge(
      {
        products: {
          [appId]: [...productIds, ...(products[appId] || [])],
        },
      },
      { deep: true }
    );
  }

  if (isType(action, addOutfits)) {
    const {
      payload: { appId, outfitIds },
    } = action;
    const { outfits } = state;

    return state.merge(
      { outfits: { [appId]: [...outfitIds, ...(outfits[appId] || [])] } },
      { deep: true }
    );
  }

  if (isType(action, updateStreamProductCount)) {
    const {
      payload: { appId, count },
    } = action;
    return state.merge(
      {
        streamProductCount: {
          [appId]: count,
        },
      },
      { deep: true }
    );
  }

  if (isType(action, changeResourceTypeAndResourceId)) {
    const {
      payload: { storyId, resourceType },
    } = action;
    if (state.story.id !== storyId) {
      return state;
    }
    return state.merge(
      {
        story: {
          resourceType: resourceType,
          resourceId: void 0,
        },
      },
      { deep: true }
    );
  }

  if (isType(action, updateTargetCategoryIsPinned)) {
    const {
      payload: { appId, isPinned, targetCategoryId },
    } = action;
    const { appConfigs } = state;
    const targetCategories =
      appConfigs[appId] && appConfigs[appId].targetCategories;

    if (!targetCategories) {
      return state;
    }

    const idxTargetCat = findIndex(propEq('id', targetCategoryId))(
      targetCategories
    );

    if (idxTargetCat === -1) {
      return state;
    }

    return state.merge(
      {
        appConfigs: {
          [appId]: {
            targetCategories: update(
              idxTargetCat,
              merge(targetCategories[idxTargetCat], { isPinned }),
              targetCategories
            ),
          },
        },
      },
      { deep: true }
    );
  }

  if (isType(action, showClusterEditLoading)) {
    return state.merge({
      loading: true,
    });
  }

  if (isType(action, newProductForAppId)) {
    const {
      payload: { appId, productId },
    } = action;
    const { appConfigs: existingAppConfigs } = state;

    return state.merge(
      {
        appConfigs: {
          [appId]: {
            product_ids: append(
              productId,
              existingAppConfigs[appId].product_ids || []
            ),
          },
        },
      },
      { deep: true }
    );
  }

  if (isType(action, updateProductIdsForAppId)) {
    const {
      payload: { appId, productIds },
    } = action;
    const { appConfigs: existingAppConfigs } = state;

    return state.merge(
      {
        appConfigs: {
          [appId]: {
            product_ids: R.union(
              existingAppConfigs[appId].product_ids || [],
              productIds
            ),
          },
        },
      },
      { deep: true }
    );
  }

  if (isType(action, newOutfitForAppId)) {
    const {
      payload: { appId, outfitId },
    } = action;
    const { appConfigs: existingAppConfigs } = state;

    return state.merge(
      {
        appConfigs: {
          [appId]: {
            outfit_ids: append(
              outfitId,
              existingAppConfigs[appId].outfit_ids || []
            ),
          },
        },
      },
      { deep: true }
    );
  }

  if (isType(action, unpublishStoryAppConfig)) {
    const {
      payload: { appId },
    } = action;
    return state.merge(
      {
        appConfigs: {
          [appId]: {
            is_active: false,
          },
        },
      },
      { deep: true }
    );
  }

  if (isType(action, removeProductIdForAppConfig)) {
    const {
      payload: { appId, productId },
    } = action;
    const { appConfigs: existingAppConfigs } = state;
    const productIds = existingAppConfigs[appId].product_ids || [];

    return state.merge(
      {
        appConfigs: {
          [appId]: {
            product_ids: reject(
              (item: number) => item === productId,
              productIds
            ),
          },
        },
      },
      { deep: true }
    );
  }

  if (isType(action, removeOutfitIdForAppConfig)) {
    const {
      payload: { appId, outfitId },
    } = action;
    const { appConfigs: existingAppConfigs } = state;
    const outfitIds = existingAppConfigs[appId].outfit_ids || [];

    return state.merge(
      {
        appConfigs: {
          [appId]: {
            outfit_ids: reject((item: number) => item === outfitId, outfitIds),
          },
        },
      },
      { deep: true }
    );
  }

  return state;
}

export function getEditCountry(state: AppState) {
  const {
    storyEdit: { activeAppId },
  } = state;

  return countryById(activeAppId);
}

export function getStoryEditStory(state: AppState) {
  const {
    storyEdit: { story },
  } = state;

  return story;
}

export function getStoryEditActiveAppId(state: AppState) {
  const {
    storyEdit: { activeAppId },
  } = state;
  return activeAppId || 139;
}

export function getLanguageGroupMasterCountryForAppId(
  state: AppState,
  appId: number
): void | Country {
  const {
    storyEdit: {
      story: { headline },
    },
  } = state;
  const country = countries.find(c => c.id === appId);

  if (!country) {
    return;
  }

  const group = LANGUAGE_GROUPS.find(g => g.includes(country.shortcode));

  if (!group || !headline) {
    return;
  }

  const filteredGroup = group.filter(c => headline[c]);

  if (!filteredGroup.length) {
    return;
  }

  const groupMasterCountry = countries.find(
    c => c.shortcode === filteredGroup[0]
  );

  if (!groupMasterCountry) {
    return;
  }

  return groupMasterCountry;
}

export function getStoryEditActiveAppConfig(state: AppState): AppConfig {
  const {
    storyEdit: { appConfigs },
  } = state;
  const activeAppId = getStoryEditActiveAppId(state);
  return (appConfigs && appConfigs[activeAppId]) || {};
}

export function getStoryActiveSourceCategories(
  state: AppState
): SourceCategory[] {
  const appId = getStoryEditActiveAppId(state);
  const appConfig = getStoryEditActiveAppConfig(state);
  if (!appConfig) {
    return [];
  }

  return map(
    (sourceCategory: SourceCategory) => {
      return {
        ...sourceCategory,
        category: getCategoryById(state, appId, sourceCategory.categoryId),
      };
    },
    appConfig.sourceCategories ? appConfig.sourceCategories : []
  );
}

export function getStoryInitialAppId(state: AppState): number | undefined {
  const appConfig = getInitialAppConfig(state);
  if (!appConfig) {
    return void 0;
  }
  return appConfig.app_id;
}

export function getStoryInitialSourceCategories(
  state: AppState
): SourceCategory[] {
  const appConfig = getInitialAppConfig(state);
  if (!appConfig) {
    return [];
  }

  return map(
    (sourceCategory: SourceCategory) => {
      return {
        ...sourceCategory,
        category: getCategoryById(
          state,
          appConfig.app_id,
          sourceCategory.categoryId
        ),
      };
    },
    appConfig.sourceCategories ? appConfig.sourceCategories : []
  );
}

export interface VisibilityDates {
  active_from: string | null;
  active_to: string | null;
  boost_from: string | null;
  boost_to: string | null;
  rebrushed_at: string | null;
}

export interface StoryEditVisiblity {
  dates: VisibilityDates;
  targetCategories?: TargetCategory[];
}

export function getStoryEditVisibility(
  state: AppState
): StoryEditVisiblity | undefined {
  const activeAppConfig = getStoryEditActiveAppConfig(state);
  const appId = getStoryEditActiveAppId(state);
  if (!activeAppConfig) {
    return;
  }
  const { targetCategories } = activeAppConfig;
  const dates = pick(
    ['active_from', 'active_to', 'boost_from', 'boost_to', 'rebrushed_at'],
    activeAppConfig
  ) as VisibilityDates;
  return {
    dates: dates,
    targetCategories: map((targetCategory: TargetCategory) => {
      if (!targetCategory) {
        return;
      }
      const sourceCategory = getCategoryById(
        state,
        appId,
        targetCategory.category_id
      );
      return { ...targetCategory, sourceCategory };
    }, targetCategories || []) as any,
  };
}

export function getActiveStoryEditTargetCategories(
  state: AppState
): TargetCategory[] {
  const activeAppConfig = getStoryEditActiveAppConfig(state);
  const { targetCategories } = activeAppConfig;
  return targetCategories || [];
}

export function getActiveStoryId(state: AppState): number {
  const {
    storyEdit: {
      story: { id },
    },
  } = state;

  return id;
}

export function isLoading(state: AppState): boolean {
  const {
    storyEdit: { loading },
  } = state;
  return loading;
}

export function getInitialCountry(state: AppState): Country {
  const {
    storyEdit: { story },
  } = state;
  const appId =
    story && story.initialAppId ? story.initialAppId : countries[0].id;

  return countryById(appId);
}

export function getInitialAppConfig(state: AppState): AppConfig {
  const {
    storyEdit: { appConfigs, story },
  } = state;

  if (!(story && story.initialAppId)) {
    return {} as AppConfig;
  }

  return (appConfigs && appConfigs[story.initialAppId]) || {};
}

export function getCountriesOfActiveAppConfig(state: AppState): Country[] {
  const {
    storyEdit: { appConfigs },
  } = state;
  return getCountriesByIds(keys(appConfigs).map(key => +key));
}

export function getChoosableCountries(state: AppState): Country[] {
  const activeCountries = getCountriesOfActiveAppConfig(state);
  return differenceWith(
    (choosable: Country, active: Country) => choosable.id === active.id,
    countries,
    activeCountries
  );
}

export function getActiveStorySourceCategories(
  state: AppState
): SourceCategory[] {
  const activeAppConfig = getStoryEditActiveAppConfig(state);
  if (!activeAppConfig) {
    return [];
  }
  const { sourceCategories } = activeAppConfig;
  return sourceCategories || [];
}

export function getActiveStoryClusterIds(
  state: AppState
): number[] | undefined {
  const { clusters } = getStoryEditStory(state);
  if (!clusters) {
    return void 0;
  }
  if (typeof clusters[0] !== 'object') {
    return clusters as number[];
  }

  return pluck('id')(clusters as Cluster[]);
}

export function getActiveStoryClusters(state: AppState): Cluster[] {
  const {
    storyEdit: { clusters, story },
  } = state;

  let data: Cluster[] = [];
  let storyClusters = (story.clusters as [number]) || [];

  forEachObjIndexed((cluster: Cluster) => data.push(cluster), clusters);
  data = data.filter(cluster => {
    return contains(cluster.id, storyClusters);
  });

  return data;
}

export function getStoryActiveAppConfigs(
  state: AppState
): { [key: number]: AppConfig } {
  const {
    storyEdit: { appConfigs },
  } = state;
  return appConfigs;
}

export function getActiveStatesByAppId(
  state: AppState
): { [key: number]: boolean } {
  const appConfigs = getStoryActiveAppConfigs(state);
  if (!appConfigs) {
    return {};
  }
  return mapObjIndexed(
    (item: AppConfig) => item.is_active || false,
    appConfigs
  );
}

export function getErrorsForActiveAppId(state: AppState): StoryError[] {
  const appConfig = getStoryEditActiveAppConfig(state);
  if (!appConfig) {
    return [];
  }

  const { errors } = appConfig;

  return errors || [];
}

export function getStoryRows(state: AppState, types?: string[]): Row[] {
  const story = getStoryEditStory(state);
  if (!story) {
    return [];
  }

  const { rows } = story;
  if (!rows) {
    return [];
  }

  return types
    ? filter((item: Row) => types.indexOf(item.type) > -1, rows)
    : rows;
}

export function getResourceIdsForStoryRows(
  state: AppState,
  elementType: string
): number[] {
  const rows = getStoryRows(state, ['tile', 'double tile', 'product row', 'outfit']);
  const productIds: number[] = [];

  forEach((item: Row) => {
    const idsForRow: number[] = pluck(
      'resourceId' as any,
      filter(
        (element: Element) => element.resourceType === elementType,
        item.elements
      ) as any
    );

    productIds.push(...idsForRow);
  }, rows);

  return productIds;
}

export function getStoryActiveAppConfigByAppId(
  state: AppState,
  appId: number
): AppConfig | undefined {
  const {
    storyEdit: { appConfigs },
  } = state;
  return appConfigs && appConfigs[appId];
}
export function getProductStreamCountForAppId(
  state: AppState,
  appId: number
): number {
  const { storyEdit } = state;
  return storyEdit &&
    storyEdit.streamProductCount &&
    storyEdit.streamProductCount[appId]
    ? storyEdit.streamProductCount[appId]
    : 0;
}

export function getTargetCategoriesByAppId(
  state: AppState,
  appId: number
): TargetCategory[] {
  const targetCategoriesLens = R.lensPath([
    'storyEdit',
    'appConfigs',
    appId,
    'targetCategories',
  ]);

  return R.view(targetCategoriesLens, state) || [];
}

export function getProductIdsByAppId(state: AppState, appId: number): number[] {
  const productIdsByAppIdLens = R.lensPath([
    'storyEdit',
    'appConfigs',
    appId,
    'product_ids',
  ]);

  return R.view(productIdsByAppIdLens, state) || [];
}



// WEBPACK FOOTER //
// ./src/store/StoryEdit/story-edit.reducer.ts